import { useEffect, useState } from 'react';
import type { Lang } from '../data/types';
import type { Route } from '../shell/routes';
import type { SiteContent, SiteWeekday } from '@risma/data-client';
import { useDashboardActions } from '../data/context';
import { t } from '../i18n/t';
import { Icon } from '../components/Icon';

type SiteHours = NonNullable<SiteContent['hours']>;

const DAY_LABELS: Record<SiteWeekday, { it: string; en: string }> = {
  mon: { it: 'Lunedì', en: 'Monday' },
  tue: { it: 'Martedì', en: 'Tuesday' },
  wed: { it: 'Mercoledì', en: 'Wednesday' },
  thu: { it: 'Giovedì', en: 'Thursday' },
  fri: { it: 'Venerdì', en: 'Friday' },
  sat: { it: 'Sabato', en: 'Saturday' },
  sun: { it: 'Domenica', en: 'Sunday' },
};
const DAY_ORDER: SiteWeekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

interface AvailabilityViewProps {
  lang: Lang;
  /** Jump to another tab — used to send the owner to the website editor / Tavoli. */
  onNavigate: (route: Route) => void;
}

export function AvailabilityView({ lang, onNavigate }: AvailabilityViewProps) {
  const actions = useDashboardActions();
  const [siteHours, setSiteHours] = useState<SiteHours | null>(null);
  const [hoursLoaded, setHoursLoaded] = useState(false);

  // The opening hours here mirror the website editor — single source of truth.
  // Bookings follow them; the owner edits them in the Sito web tab.
  useEffect(() => {
    let alive = true;
    actions
      .loadSite()
      .then((s) => {
        if (!alive) return;
        setSiteHours(s.draft?.hours ?? s.published?.hours ?? null);
        setHoursLoaded(true);
      })
      .catch(() => alive && setHoursLoaded(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="view">
      <div className="view-pad">
        <div className="setwrap" style={{ gridTemplateColumns: '1fr', maxWidth: 820 }}>
          <div>
            <div className="set-block">
              <h2>{t('openHours', lang)}</h2>
              <div className="sub">{t('bookingFollowsHours', lang)}</div>
              {siteHours ? (
                <div className="panel" style={{ padding: '4px 18px' }}>
                  {DAY_ORDER.map((key) => {
                    const day = (siteHours.week ?? []).find((d) => d.day === key);
                    const ranges = day?.ranges ?? [];
                    const closed = !day || day.closed || ranges.length === 0;
                    return (
                      <div className="hours-row" key={key}>
                        <span className="day">{DAY_LABELS[key][lang]}</span>
                        <span className="hours-slots">
                          {closed ? (
                            <span className="closed">{t('closed', lang)}</span>
                          ) : (
                            ranges.map((r, j) => (
                              <span className="hslot" key={j}>
                                <span className="tnum">
                                  {r.open}–{r.close}
                                </span>
                              </span>
                            ))
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : hoursLoaded ? (
                <div className="sub" style={{ marginTop: 8 }}>
                  {t('hoursNotSet', lang)}
                </div>
              ) : null}
              {siteHours?.specialNote && (
                <div className="sub" style={{ marginTop: 10 }}>
                  {siteHours.specialNote}
                </div>
              )}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ marginTop: 14 }}
                onClick={() => onNavigate('website')}
              >
                <Icon name="edit" size={14} />
                {t('editInWebsite', lang)}
              </button>
            </div>

            {/* Zone e tavoli si gestiscono SOLO nella sezione "Tavoli" (dati veri,
                collegati al calendario). Qui un rimando, per non duplicare. */}
            <div className="set-block">
              <h2>{lang === 'it' ? 'Tavoli' : 'Tables'}</h2>
              <div className="sub">
                {lang === 'it'
                  ? 'Zone e tavoli (quelli usati dal calendario) si gestiscono nella sezione Tavoli.'
                  : 'Zones and tables (the ones the calendar uses) are managed in the Tables section.'}
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ marginTop: 14 }}
                onClick={() => onNavigate('tables')}
              >
                <Icon name="utensils" size={14} />
                {lang === 'it' ? 'Vai a Tavoli' : 'Go to Tables'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
